import { useEffect, useState } from 'react';
import { cn } from '../../../../../../lib/utils';
import { cardClass, primaryText, secondaryText, semiBoldText, smallTextSize } from '../../../../../../styling/stylingHelper';
import { getAICheckerKnownCategories } from '../../../../../../services/admin/adminService';
import type { TFn } from '../../types';

/**
 * "Pick a known-to-do category" modal — lists the existing categories to click, plus a free-text field
 * to create a new one. Used when marking an email or a quantity flag known. `onConfirm` receives the
 * chosen category label.
 */
export function KnownCategoryModal({ title, subtitle, onConfirm, onClose, t }: {
  title: string; subtitle?: string; onConfirm: (category: string) => Promise<void> | void; onClose: () => void; t: TFn;
}) {
  const [categories, setCategories] = useState<string[]>([]);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getAICheckerKnownCategories()
      .then((c) => { if (!cancelled) setCategories(c); })
      .catch(() => { /* picker still works with free text */ });
    return () => { cancelled = true; };
  }, []);

  const submit = async (category: string) => {
    const c = category.trim();
    if (!c || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm(c);
      onClose();
    } catch (e: any) {
      setError(e.message || 'Failed');
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/50 p-4" onClick={onClose}>
      <div className={cn(cardClass, 'w-full max-w-md p-6 space-y-4')} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className={cn(primaryText, semiBoldText)}>{title}</h3>
            {subtitle && <p className={cn(secondaryText, 'text-xs mt-1')}>{subtitle}</p>}
          </div>
          <button onClick={onClose} className={cn(secondaryText, 'hover:opacity-70')} aria-label="Close">✕</button>
        </div>

        {error && <div className="text-xs text-red-600 dark:text-red-400">{error}</div>}

        {categories.length > 0 && (
          <div>
            <div className={cn(secondaryText, 'text-[10px] uppercase tracking-wide mb-1.5')}>{t('adminTesting.review.knownPickExisting')}</div>
            <div className="flex flex-wrap gap-2">
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => submit(c)}
                  disabled={busy}
                  className={cn('px-3 py-1 rounded-full border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/40 disabled:opacity-50', primaryText, 'text-xs')}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        )}

        <div>
          <div className={cn(secondaryText, 'text-[10px] uppercase tracking-wide mb-1.5')}>{t('adminTesting.review.knownNewCategory')}</div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={value}
              autoFocus
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') submit(value); }}
              placeholder={t('adminTesting.review.knownNewCategoryPlaceholder')}
              className={cn('flex-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700', primaryText, smallTextSize)}
            />
            <button
              onClick={() => submit(value)}
              disabled={!value.trim() || busy}
              className={cn('px-4 py-2 rounded-lg bg-purple-600 dark:bg-purple-600 text-white dark:text-white hover:bg-purple-700 dark:hover:bg-purple-700 disabled:opacity-40 disabled:cursor-not-allowed', smallTextSize, semiBoldText)}
            >
              {t('adminTesting.review.knownConfirm')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
